import Vue from 'vue';
import Router from 'vue-router';
import AuthCallback from './views/AuthCallback.vue';
import store from './store/store';
import Analytics from './Analytics';
import authStore from './store/modules/authentication';
import { PATH } from './constants/common';

Vue.use(Router);

let routes;
if (process.env.VUE_APP_REPORT_MODE) {
  routes = [
    { path: '/', redirect: '/report' },
    {
      path: '/report',
      component: () => import('./views/Report.vue')
    }
  ];
} else {
  routes = [
    // adjust this to your need
    {
      path: '/',
      component: () => import('./views/Home.vue'),
      meta: {
        isPublic: true
      }
    },
    {
      path: '/welcome',
      name: 'welcome',
      component: () => import('./views/Landing.vue'),
      meta: {
        isPublic: true
      }
    },
    {
      path: '/suspicious-traffic',
      name: 'suspicious-traffic',
      component: () => import('./views/Overview.vue')
    },
    {
      path: '/routing-status',
      name: 'routing-status',
      component: () => import('./views/RoutingStatus.vue')
    },
    {
      path: '/learn-more',
      name: 'learn-more',
      component: () => import('./views/LearnMore.vue'),
      meta: {
        isPublic: true
      }
    },
    {
      path: '/help',
      name: 'help',
      component: () => import('./views/Help.vue')
    },
    {
      path: '/security',
      name: 'security',
      component: () => import('./views/Security.vue')
    },
    {
      path: '/disclaimer',
      name: 'disclaimer',
      component: () => import('./views/Disclaimer.vue')
    },
    {
      path: '/methodology',
      name: 'methodology',
      component: () => import('./views/Methodology.vue')
    },
    {
      path: '/data-source',
      name: 'data-source',
      component: () => import('./views/DataSource.vue')
    },
    {
      path: '/what-to-do',
      name: 'what-to-do',
      component: () => import('./views/WhatToDo.vue')
    },
    {
      path: '/alerts-suspicious-traffic',
      name: 'alerts-suspicious-traffic',
      component: () => import('./views/AlertsSuspiciousTraffic.vue')
    },
    {
      path: '/alerts-routing-status',
      name: 'alerts-routing-status',
      component: () => import('./views/AlertsRoutingStatus.vue')
    },
    {
      path: '/auth-callback', // Needs to match redirect_uri in you oidcSettings
      name: 'authCallback',
      component: AuthCallback,
      meta: {
        isOidcCallback: true,
        isPublic: true
      }
    },
    // leave this at the bottom, also, this should be a temporary solution to handle 404 request
    { path: '*', redirect: '/routing-status' }
  ];
}

// This page is only for mocking data.
if (process.env.VUE_APP_MOCK_DATA_PAGE) {
  routes.push({
    path: '/mock-data',
    name: 'mockData',
    component: () => import('./views/MockData.vue')
  });
}

const router = new Router({
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0 };
  },
  mode: 'history',
  base: process.env.BASE_URL,
  routes
});

if (!process.env.VUE_APP_REPORT_MODE) {
  // Set the flag of isPopStateChanged to verify whether URL is updated due to browser history change.
  window.isPopStateChanged = false;
  window.addEventListener('popstate', () => {
    window.isPopStateChanged = true;
  });

  router.beforeEach((to, from, next) => {
    // Set selected member account according to URL query of "mem" when route is changed due to browser history change.
    if (window.isPopStateChanged) {
      // window.isPopStateChanged will be used by method selectAccount in dataStore, so it is reset within the setTimeout callback (i.e. at the end of browser event loop).
      setTimeout(() => {
        window.isPopStateChanged = false;
      }, 0);

      // Get selected member account.
      const selectedAccount =
        store.getters['dataStore/selectedAccount']?.account;

      // Get member account in URL query.
      const memberAccountInQuery = to.query.mem;

      // Set selected member account if it's different from the one in URL query.
      if (memberAccountInQuery && memberAccountInQuery !== selectedAccount) {
        const isAPNICStaff = store.state['isAPNICStaff'];

        if (isAPNICStaff) {
          store.dispatch('dataStore/searchMemberAccount', memberAccountInQuery);
        } else {
          const accountList = store.getters['dataStore/accountList'] || [];

          // Get corresponding member Id from member account list according to the account in URL query.
          const memberId = accountList.find(
            (i) => i.account === memberAccountInQuery
          )?.id;

          if (memberId) {
            store.dispatch('dataStore/selectAccount', memberId);
          }
        }
      }
    }

    if (
      from.name !== 'authCallback' &&
      to.name !== 'welcome' &&
      to.name !== 'authCallback'
    )
      storeCurrentPath(to.fullPath);

    if (from.name == 'authCallback' && to.name === 'routing-status') {
      store.commit('setIsNavigatingToRSPageAfterLogin', true);
    } else {
      store.commit('setIsNavigatingToRSPageAfterLogin', false);
    }

    if (
      to.meta.isPublic ||
      authStore.getters.oidcIsAuthenticated(authStore.state) ||
      from.name == 'authCallback'
    ) {
      return next();
    } else {
      checkAuthStatus(next);
    }
  });

  router.afterEach((to) => {
    // Each in-page navigation will trigger a new page view event.
    Analytics.trackPageView({ pageURL: to.path });

    checkSelectedASN(to);
  });
}

// Switching to pages of suspicious-traffic category will trigger this function to check if selected ASN is null, in which case the first ASN in ASN list will be selected.
const checkSelectedASN = (to) => {
  const isToSuspiciousTrafficCategoryPages = [
    PATH.SUSPICIOUS_TRAFFIC,
    PATH.ALERTS_SUSPICIOUS_TRAFFIC,
    PATH.REQUEST_INSIGHTS_OF_SUSPICIOUS_TRAFFIC
  ].includes(to.name);

  const selectedASN = store.getters['dataStore/selectedASN'];

  if (isToSuspiciousTrafficCategoryPages && !selectedASN) {
    const selectedAccount = store.getters['dataStore/selectedAccount'];

    if (
      selectedAccount &&
      selectedAccount.ASNs &&
      selectedAccount.ASNs[0] &&
      selectedAccount.ASNs[0].id
    ) {
      store.dispatch('dataStore/selectASN', selectedAccount.ASNs[0].id);
    }
  }
};

const checkAuthStatus = (next) => {
  const oidcAuthenticationIsCheckedPromise = () =>
    new Promise((resolve) => {
      store.commit('setIsCheckingAuthentication', true);

      const oidcAuthenticationIsChecked =
        store.getters['authStore/oidcAuthenticationIsChecked'];

      if (oidcAuthenticationIsChecked) {
        resolve(true);
      } else {
        resolve(false);
      }
    });

  oidcAuthenticationIsCheckedPromise()
    .then((res) => {
      if (res === false) {
        setTimeout(() => {
          checkAuthStatus(next);
        }, 200);
      } else {
        store.commit('setIsCheckingAuthentication', false);
        const oidcIsAuthenticated =
          store.getters['authStore/oidcIsAuthenticated'];

        if (oidcIsAuthenticated) {
          next();
        } else {
          next('/welcome');
        }
      }
    })
    .catch((err) => {
      console.error('checkAuthStatus error: ', err);
      store.commit('setIsCheckingAuthentication', false);
      next('/welcome');
    });
};

// Store current path in sessionStorage as prev_path, which will be read in AuthCallback when redirected back from SSO login.
function storeCurrentPath(path) {
  sessionStorage.setItem('prev_path', path);
}

export default router;
